import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Label } from "@/components/ui/label";
import { FilePlus, Loader2, User, Calendar, DollarSign, Globe, Building2, Tag, Info } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import { SearchableSelect } from "@/components/ui/searchable-select";

interface AddKolDbDataDialogProps {
  onDataAdded?: () => void;
}

export function AddKolDbDataDialog({ onDataAdded }: AddKolDbDataDialogProps) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [actualUserName, setActualUserName] = useState<string>('');
  const [formData, setFormData] = useState({
    creator_name: "",
    social_media_type: "",
    social_media_handler: "",
    region: "",
    tier: "",
    start_date: "",
    end_date: "",
  });
  
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const SOCIAL_MEDIA_OPTIONS = ['Youtube', 'Twitter', 'Telegram', 'Other'];
  const REGION_OPTIONS = ['IN', 'TR', 'CN', 'JP', 'KR', 'ID', 'ES', 'VN', 'RU', 'EN'];
  const TIER_OPTIONS = ['Tier 0', 'Tier 1', 'Tier 2', 'Tier 3'];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const { hasFullAccess, userName } = useAuth();

  // Fetch current user ID and name on component mount
  useEffect(() => {
    const getCurrentUserInfo = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user?.id) {
          setCurrentUserId(session.user.id);
          
          // Get the actual user name with correct capitalization from kol_pic table
          if (!hasFullAccess) {
            const { data: picData, error: picError } = await supabase
              .from('kol_pic')
              .select('name')
              .eq('id', session.user.id)
              .single();
            
            if (!picError && picData) {
              const correctUserName = picData.name;
              setActualUserName(correctUserName);
              console.log('Setting correct user name for limited user:', correctUserName);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching current user info:', error);
      }
    };

    getCurrentUserInfo();
  }, [hasFullAccess]);

  function getPicArrayForInsert() {
    if (hasFullAccess) return null;
    // For limited access users, always include their user name (not ID)
    return actualUserName ? [actualUserName] : [];
  }
  
  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    setFormData(prev => ({
      ...prev,
      start_date: date ? date.toISOString().split('T')[0] : ''
    }));
  };
  
  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
    setFormData(prev => ({
      ...prev,
      end_date: date ? date.toISOString().split('T')[0] : ''
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.creator_name) {
      toast({
        title: "Required Field Missing",
        description: "Creator name is required",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      console.log('Submitting with PIC array:', getPicArrayForInsert());
      console.log('Actual user name:', actualUserName);
      
      const { data, error } = await supabase
        .from('kol_db')
        .insert({
          creator_name: formData.creator_name,
          social_media_type: formData.social_media_type,
          social_media_handler: formData.social_media_handler,
          // Hidden fields will be null/empty when inserted
          social_media_name: null,
          payment_structure: null,
          currency_type: null,
          region: formData.region,
          tier: formData.tier,
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
          pic_id: getPicArrayForInsert(),
        }).select();

      if (error) {
        console.error("Error details:", error);
        throw error;
      }

      toast({
        title: "Success",
        description: "KOL data added successfully",
      });
      
      setFormData({
        creator_name: "",
        social_media_type: "",
        social_media_handler: "",
        region: "",
        tier: "",
        start_date: "",
        end_date: "",
      });
      
      setStartDate(null);
      setEndDate(null);
      
      if (onDataAdded) onDataAdded();
      setOpen(false);
    } catch (error: any) {
      console.error("Error adding data:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to add KOL data",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline"
          size="sm"
          className="h-9"
        >
          <FilePlus className="h-4 w-4 mr-2" />
          Add New KOL
        </Button>
      </DialogTrigger>
      <DialogContent 
        className={cn(
          "sm:max-w-[700px] max-h-[85vh] overflow-y-auto",
          "shadow-2xl border-0",
          isLightMode 
            ? "bg-white text-gray-900" 
            : "bg-gradient-to-br from-[#1A1A1A] to-[#151515] text-white"
        )}
        style={{ 
          zIndex: 9100, 
          pointerEvents: 'auto'
        }}
      >
        <DialogHeader className="space-y-3 pb-4">
          <div className="flex items-center space-x-3">
            <div className={cn(
              "p-2 rounded-full",
              isLightMode ? "bg-blue-100" : "bg-[#9b87f5]/20"
            )}>
              <User className={cn(
                "h-5 w-5",
                isLightMode ? "text-blue-600" : "text-[#9b87f5]"
              )} />
            </div>
            <div>
              <DialogTitle className={cn(
                "text-xl font-bold",
                isLightMode ? "text-gray-900" : "text-white"
              )}>
                Add New KOL
              </DialogTitle>
              <DialogDescription className={cn(
                "text-xs mt-1",
                isLightMode ? "text-gray-600" : "text-gray-400"
              )}>
                Add a new content creator to your database. Fields marked with * are required.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        {!hasFullAccess && (
          <div className={cn(
            "p-3 rounded-lg border-l-4 mb-4 flex items-start space-x-2",
            isLightMode 
              ? "bg-amber-50 border-amber-400 text-amber-800" 
              : "bg-amber-900/20 border-amber-500 text-amber-300"
          )}>
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div className="text-xs">
              <p className="font-medium">Limited Access Notice</p>
              <p className="mt-1 opacity-90">
                As a limited access user, you can only add yourself as PIC for new KOL entries.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <User className={cn(
                "h-4 w-4",
                isLightMode ? "text-blue-600" : "text-[#9b87f5]"
              )} />
              <h3 className={cn(
                "text-base font-semibold",
                isLightMode ? "text-gray-900" : "text-white"
              )}>
                Basic Information
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="creator_name" className={cn(
                  "text-xs font-medium flex items-center space-x-1",
                  isLightMode ? "text-gray-700" : "text-gray-200"
                )}>
                  <span>Creator Name</span>
                  <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="creator_name"
                  name="creator_name"
                  value={formData.creator_name}
                  onChange={handleInputChange}
                  placeholder="Enter the creator's full name"
                  required
                  className={cn(
                    "h-10 transition-all duration-200 focus:ring-2 text-sm",
                    isLightMode 
                      ? "bg-gray-50 border-gray-200 focus:bg-white focus:ring-blue-500/20 focus:border-blue-500" 
                      : "bg-[#27292B] border-[#444547] focus:bg-[#2A2C2E] focus:ring-[#9b87f5]/20 focus:border-[#9b87f5]"
                  )}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="social_media_type" className={cn(
                  "text-xs font-medium",
                  isLightMode ? "text-gray-700" : "text-gray-200"
                )}>
                  Social Media Platform
                </Label>
                <SearchableSelect
                  options={SOCIAL_MEDIA_OPTIONS}
                  value={formData.social_media_type}
                  onChange={(value) => handleSelectChange('social_media_type', value)}
                  placeholder="Select platform"
                  badgeVariant="blue"
                  clearable={true}
                  className="w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="social_media_handler" className={cn(
                  "text-xs font-medium",
                  isLightMode ? "text-gray-700" : "text-gray-200"
                )}>
                  Social Media Handle
                </Label>
                <Input
                  id="social_media_handler"
                  name="social_media_handler"
                  value={formData.social_media_handler}
                  onChange={handleInputChange}
                  placeholder="@username or handle"
                  className={cn(
                    "h-10 transition-all duration-200 focus:ring-2 text-sm",
                    isLightMode 
                      ? "bg-gray-50 border-gray-200 focus:bg-white focus:ring-blue-500/20 focus:border-blue-500" 
                      : "bg-[#27292B] border-[#444547] focus:bg-[#2A2C2E] focus:ring-[#9b87f5]/20 focus:border-[#9b87f5]"
                  )}
                />
              </div>
            </div>
          </div>

          <Separator className={isLightMode ? "bg-gray-200" : "bg-gray-700"} />

          {/* Region & Tier Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Globe className={cn(
                "h-4 w-4",
                isLightMode ? "text-purple-600" : "text-purple-400"
              )} />
              <h3 className={cn(
                "text-base font-semibold",
                isLightMode ? "text-gray-900" : "text-white"
              )}>
                Region & Tier
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="region" className={cn(
                  "text-xs font-medium",
                  isLightMode ? "text-gray-700" : "text-gray-200"
                )}>
                  Region
                </Label>
                <SearchableSelect
                  options={REGION_OPTIONS}
                  value={formData.region}
                  onChange={(value) => handleSelectChange('region', value)}
                  placeholder="Select region"
                  badgeVariant="purple"
                  clearable={true}
                  className="w-full"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="tier" className={cn(
                  "text-xs font-medium",
                  isLightMode ? "text-gray-700" : "text-gray-200"
                )}>
                  Tier Level
                </Label>
                <SearchableSelect
                  options={TIER_OPTIONS}
                  value={formData.tier}
                  onChange={(value) => handleSelectChange('tier', value)}
                  placeholder="Select tier"
                  badgeVariant="dark"
                  clearable={true}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          <Separator className={isLightMode ? "bg-gray-200" : "bg-gray-700"} />

          {/* Contract Period Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Calendar className={cn(
                "h-4 w-4",
                isLightMode ? "text-indigo-600" : "text-indigo-400"
              )} />
              <h3 className={cn(
                "text-base font-semibold",
                isLightMode ? "text-gray-900" : "text-white"
              )}>
                Contract Period
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date" className={cn(
                  "text-xs font-medium",
                  isLightMode ? "text-gray-700" : "text-gray-200"
                )}>
                  Start Date
                </Label>
                <Input
                  id="start_date"
                  name="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => {
                    try {
                      const dateValue = e.target.value ? new Date(e.target.value) : null;
                      handleStartDateChange(dateValue);
                    } catch (err) {
                      console.error("Invalid date:", err);
                    }
                  }}
                  className={cn(
                    "h-10 transition-all duration-200 focus:ring-2 text-sm",
                    isLightMode 
                      ? "bg-gray-50 border-gray-200 focus:bg-white focus:ring-indigo-500/20 focus:border-indigo-500" 
                      : "bg-[#27292B] border-[#444547] focus:bg-[#2A2C2E] focus:ring-indigo-400/20 focus:border-indigo-400"
                  )}
                  style={{ position: 'relative', zIndex: 10, pointerEvents: 'auto' }}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="end_date" className={cn(
                  "text-xs font-medium",
                  isLightMode ? "text-gray-700" : "text-gray-200"
                )}>
                  End Date
                </Label>
                <Input
                  id="end_date"
                  name="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => {
                    try {
                      const dateValue = e.target.value ? new Date(e.target.value) : null;
                      handleEndDateChange(dateValue);
                    } catch (err) {
                      console.error("Invalid date:", err);
                    }
                  }}
                  className={cn(
                    "h-10 transition-all duration-200 focus:ring-2 text-sm",
                    isLightMode 
                      ? "bg-gray-50 border-gray-200 focus:bg-white focus:ring-indigo-500/20 focus:border-indigo-500" 
                      : "bg-[#27292B] border-[#444547] focus:bg-[#2A2C2E] focus:ring-indigo-400/20 focus:border-indigo-400"
                  )}
                  style={{ position: 'relative', zIndex: 10, pointerEvents: 'auto' }}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex flex-col sm:flex-row gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className={cn(
                "h-10 px-6 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] text-sm",
                isLightMode 
                  ? "bg-white border-gray-300 hover:bg-gray-50 text-gray-700 hover:border-gray-400" 
                  : "bg-[#27292B] hover:bg-[#32353a] border-[#444547] text-white hover:border-[#555]"
              )}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className={cn(
                "h-10 px-6 relative overflow-hidden transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]",
                "shadow-lg hover:shadow-xl disabled:hover:scale-100 text-sm font-medium",
                isLightMode 
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white border-0" 
                  : "bg-gradient-to-r from-[#9b87f5] to-[#8B7EC8] hover:from-[#8B7EC8] hover:to-[#7E69AB] text-white border-0"
              )}
            >
              <div className="absolute inset-0 bg-white/10 opacity-0 hover:opacity-100 transition-opacity duration-200" />
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Adding KOL...
                </>
              ) : (
                <>
                  <FilePlus className="mr-2 h-4 w-4" />
                  Add KOL
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
